import { Match } from "@/models/match/Match";
import { OUTCOMES } from "@/constants/outcomes";
import { finishedTeamConstructor } from "@/utils/constructors/finishedTeamConstructor";
import { calculateFinalMatch } from "@/utils/match/resultMatchCalculator";

/**
 * Renvoi les données de l'équipe de l'utilisateur dans la journée, le côté
 * qu'elle occupait et l'adversaire qu'elle a réellement affronté
 * @param {Array} divisionMatches les matchs de la journée
 * @param {String} userTeamId l'identifiant de l'équipe de l'utilisateur
 * @returns {Object|undefined} les données de l'équipe, son côté et son vrai adversaire
 */
const findUserTeam = (divisionMatches, userTeamId) => {
  for (const divisionMatch of divisionMatches) {
    if (divisionMatch.home.teamId === userTeamId) {
      return {
        teamData: divisionMatch.home,
        side: "home",
        realOpponentTeamId: divisionMatch.away.teamId,
      };
    }
    if (divisionMatch.away.teamId === userTeamId) {
      return {
        teamData: divisionMatch.away,
        side: "away",
        realOpponentTeamId: divisionMatch.home.teamId,
      };
    }
  }
  return undefined;
};

/**
 * Renvoi les données de toutes les équipes de la journée sauf celle de l'utilisateur
 * @param {Array} divisionMatches les matchs de la journée
 * @param {String} userTeamId l'identifiant de l'équipe de l'utilisateur
 * @returns {Array} les données des adversaires possibles
 */
const getOpponentsTeamsData = (divisionMatches, userTeamId) => {
  return divisionMatches
    .flatMap((divisionMatch) => [divisionMatch.home, divisionMatch.away])
    .filter((teamData) => teamData.teamId !== userTeamId);
};

/**
 * Renvoi si les données d'une équipe permettent de rejouer sa journée
 * Une équipe en remplacements live compose son banc avec tout son effectif :
 * si un entrant de l'historique n'y figure pas, le replay produirait une
 * composition fausse sans rien signaler. Une équipe en remplacements
 * tactiques n'a besoin d'aucune vérification supplémentaire.
 * @param {Object} teamData les données d'une équipe dans divisionMatches
 * @returns {Boolean} true si l'équipe est exploitable
 */
const isTeamDataUsable = (teamData) => {
  if (teamData.isLiveSubstitutesEnabled !== true) {
    return true;
  }
  if (! Array.isArray(teamData.liveSubstitutesHistory)) {
    return false;
  }
  return teamData.liveSubstitutesHistory.every(({ subId }) => Boolean(teamData.players?.[subId]));
};

/**
 * Renvoi si toute la journée est exploitable
 * Une seule équipe inexploitable suffit à écarter la journée : une liste
 * partielle ferait disparaître un adversaire sans explication.
 * @param {Array} divisionMatches les matchs de la journée
 * @returns {Boolean} true si toutes les équipes sont exploitables
 */
const isGameWeekUsable = (divisionMatches) => {
  return divisionMatches
    .flatMap((divisionMatch) => [divisionMatch.home, divisionMatch.away])
    .every(isTeamDataUsable);
};

/**
 * Renvoi l'issue d'un match du point de vue de l'utilisateur
 * @param {Array} score le score du match, domicile puis extérieur
 * @param {String} userSide le côté occupé par l'utilisateur, "home" ou "away"
 * @returns {String} une valeur de OUTCOMES
 */
const getOutcome = ([homeGoals, awayGoals], userSide) => {
  if (homeGoals === awayGoals) {
    return OUTCOMES.DRAW;
  }
  const isUserWinning = userSide === "home" ? homeGoals > awayGoals : awayGoals > homeGoals;
  return isUserWinning ? OUTCOMES.WIN : OUTCOMES.LOSS;
};

/**
 * Compose et calcule une confrontation entre l'équipe de l'utilisateur et un adversaire
 * @param {Object} userTeam l'équipe de l'utilisateur, son côté et son vrai adversaire
 * @param {Object} opponentTeamData les données de l'adversaire
 * @param {Object} options le mode aller simple et les noms d'équipes
 * @returns {Object} la confrontation calculée
 */
const buildEntry = (userTeam, opponentTeamData, { isOneWay, teamNames }) => {
  const user = finishedTeamConstructor(userTeam.teamData);
  const opponent = finishedTeamConstructor(opponentTeamData);
  user.name = teamNames.get(user.id) || user.name;
  opponent.name = teamNames.get(opponent.id) || opponent.name;

  const isUserAtHome = userTeam.side === "home";
  const id = `${user.id}_vs_${opponent.id}`;

  const match = new Match({
    id,
    isOneWay,
    homeTeam: isUserAtHome ? user : opponent,
    awayTeam: isUserAtHome ? opponent : user,
  });

  const finalMatch = calculateFinalMatch(match);

  return {
    id,
    opponentTeamId: opponent.id,
    isRealMatch: opponent.id === userTeam.realOpponentTeamId,
    outcome: getOutcome(finalMatch.score, userTeam.side),
    match: finalMatch,
  };
};

/**
 * Renvoi les résultats qu'aurait obtenus l'équipe de l'utilisateur face à chaque
 * adversaire de la journée, bonus retirés de part et d'autre
 * Le vrai match, recalculé de la même manière, est placé en tête.
 * @param {Object} params les matchs de la journée, l'équipe de l'utilisateur, le mode aller simple et les noms d'équipes
 * @returns {Array} les confrontations calculées
 */
const buildAlternativeMatches = ({ divisionMatches, userTeamId, isOneWay = false, teamNames = new Map() }) => {
  if (! isGameWeekUsable(divisionMatches)) {
    return [];
  }

  const userTeam = findUserTeam(divisionMatches, userTeamId);
  if (! userTeam) {
    return [];
  }

  return getOpponentsTeamsData(divisionMatches, userTeamId)
    .map((opponentTeamData) => buildEntry(userTeam, opponentTeamData, {
      isOneWay,
      teamNames,
    }))
    .sort((first, second) => Number(second.isRealMatch) - Number(first.isRealMatch));
};

export { buildAlternativeMatches };